import React from 'react';
import clsx from 'clsx';
import type { DiagnosticErrorProps, DiagnosticErrorItem } from './DiagnosticError.types';

const DiagnosticErrorItemMobile: React.FC<{ error: DiagnosticErrorItem }> = ({
  error,
}) => (
  <div className="relative">
    <span className="text-sm font-semibold text-text-primary">Kod błędu</span>
    <div className="mt-2">
      <div className="relative z-10 px-6 py-2 text-sm font-semibold tracking-wider leading-none text-center text-red-600 uppercase bg-red-50 rounded-full w-fit min-w-[180px]">
        {error.errorCode}
      </div>
    </div>

    {/* Pionowa linia łącząca */}
    <div className="absolute left-[88px] top-[52px] w-[1px] h-[calc(100%-52px-16px)] bg-grayBorder" />

    <div className="flex items-center mt-6">
      <span className="text-sm font-semibold text-text-primary">Opis błędu</span>
      <div className="w-4 h-[1px] bg-grayBorder ml-1 hidden lg:block" />
    </div>

    <div className="mt-2 p-4 text-sm leading-relaxed text-gray-600 border border-grayBorder rounded-[15px] bg-white relative z-10">
      {error.description}
    </div>
  </div>
);

const DiagnosticErrorItemDesktop: React.FC<{ error: DiagnosticErrorItem }> = ({
  error,
}) => (
  <div className="grid grid-cols-[185px_1fr] gap-6">
    <div className="flex relative items-center">
      <div className="px-8 py-2 w-full text-sm font-semibold tracking-wider leading-none text-center text-red-600 uppercase bg-red-50 rounded-full">
        {error.errorCode}
      </div>
      <div className="bg-grayBorder absolute left-[100%] top-1/2 h-[1px] w-6 -translate-y-1/2"></div>
    </div>
    <div className="p-4 text-sm leading-relaxed text-gray-600 border border-grayBorder rounded-card">
      {error.description}
    </div>
  </div>
);

/**
 * Komponent do wyświetlania błędów diagnostycznych z kodem błędu i opisem
 *
 * @example
 * ```tsx
 * <DiagnosticError
 *   errors={[
 *     {
 *       errorCode: "94XWUo",
 *       description: "Sterownik SRS > Zestyk zamka pasa z tyłu z prawej strony > Przerwa"
 *     },
 *     {
 *       errorCode: "XU8930",
 *       description: "Brak informacji"
 *     }
 *   ]}
 * />
 * ```
 */
export const DiagnosticError: React.FC<DiagnosticErrorProps> = ({
  errors,
  className,
}) => {
  return (
    <div className={clsx('overflow-hidden relative p-6 w-full', className)}>
      {/* Mobile Layout */}
      <div className="space-y-6 lg:hidden">
        {errors.map((error, index) => (
          <DiagnosticErrorItemMobile
            key={`mobile-${error.errorCode}-${index}`}
            error={error}
          />
        ))}
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:block">
        {/* Wspólny nagłówek kolumn */}
        <div className="mb-4 grid grid-cols-[180px_1fr] gap-10">
          <div className="text-sm font-semibold text-center text-gray-900">
            Kod błędu
          </div>
          <div className="pl-4 text-sm font-semibold text-left text-gray-900">
            Opis błędu
          </div>
        </div>

        {/* Lista błędów */}
        <div className="space-y-4">
          {errors.map((error, index) => (
            <DiagnosticErrorItemDesktop
              key={`desktop-${error.errorCode}-${index}`}
              error={error}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
